import { useTranslation } from 'react-i18next';
import { Copy, ExternalLink, Link2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

export function LinkCard({ url }: { url: string }) {
  const { t } = useTranslation('adminBookingPage');
  const { toast } = useToast();
  const copy = async () => {
    await navigator.clipboard?.writeText(url);
    toast({ title: t('link.copied') });
  };
  const share = async () => {
    if (navigator.share) await navigator.share({ url }).catch(() => undefined);
    else await copy();
  };

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-line bg-surface p-4 shadow-card sm:flex-row sm:items-center sm:gap-4 sm:px-[22px]" data-testid="link-card">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 sm:inline-flex">
          <Link2 className="h-[18px] w-[18px] text-brand-600" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-body-sm text-soft">{t('link.label')}</span>
          <span className="truncate text-[15px] font-semibold text-ink" data-testid="public-url">{url.replace(/^https?:\/\//, '')}</span>
        </div>
        <span className="ml-auto inline-flex h-7 items-center gap-1.5 rounded-pill bg-success-soft px-3 text-[13px] font-medium text-success-text">
          <span className="h-[7px] w-[7px] rounded-pill bg-success" aria-hidden="true" />
          {t('link.live')}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:flex">
        <Button variant="outline" className="h-11 sm:h-10" onClick={copy}><Copy className="h-4 w-4" />{t('link.copy')}</Button>
        <Button variant="outline" className="h-11 sm:hidden" onClick={share}><Share2 className="h-4 w-4" />{t('link.share')}</Button>
        <Button asChild variant="outline" className="h-11 sm:h-10">
          <a href={url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" />{t('link.open')}</a>
        </Button>
      </div>
    </section>
  );
}
